import * as Sentry from "@sentry/react";
import { sanitizeErrorForLog } from "./sanitizeErrorForLog";

// Decides which console.error calls are worth a support alert.
//
// Why this exists at the console layer rather than at 400+ call sites: inside the
// mobile app this bundle runs in a WebView whose console is overridden by the native
// shell (lucasai-app/app/WebViewPage.js), and EVERY console.error is forwarded to
// native, re-logged as Logger.error("WEB: …"), and turned into an ArcadeBrain org
// event — which mails support@ when it lands at WARNING/SEVERE. So a `catch`
// anywhere in this app is a support email, and the two cheapest failures in
// clinical practice generate them all day:
//
//   - an expired session cookie: the resync queue retries every 20s→5min for as long
//     as a recording stays queued (deliberately — it must never drop audio), so ONE
//     logged-out device mailed support indefinitely;
//   - a dropped connection: rural/hospital wifi, nothing broken server-side.
//
// Neither is actionable by support, and both drown the reports that are. Filtering at
// the console is the only lever that covers every existing call site plus every one
// written later, without editing 400 catch blocks and hoping the next one remembers.
//
// Three verdicts:
//   REPORT — untouched: real console.error (Sentry event + native forward).
//   QUIET  — a Sentry BREADCRUMB instead of an event, and never forwarded to native.
//            Breadcrumbs cost no quota and are strictly more useful than an event:
//            when a genuine error follows, the trail shows the flakiness that led to
//            it. Nothing is hidden from developers — outside the WebView it still
//            prints (as a warning) to the browser console.
//   DROP   — noise with no diagnostic value at all.
//
// The same classification exists server-side in lucasai-backend's
// lib/clientEventPolicy.js, and it has to: app builds live on phones for months, so
// this filter fixes new installs while that one covers the ones already out there.
// Keep the two lists in step when adding a pattern.

export const REPORT = "report";
export const QUIET = "quiet";
export const DROP = "drop";

// An expired/missing session, or a request the caller is not allowed to make. The app
// already tells the clinician to sign in again (useIndexDb's sessionExpiredRelogin
// toast) — that is the actionable half, and it is a UI concern, not an alert.
const AUTH_PATTERNS = [
  /no challenge/i,
  /access forbidden/i,
  /response:\s*40[13]\b/i,
  /\bunauthori[sz]/i,
  /session\s*(has\s*)?expired/i,
  /not authenticated/i,
  /invalid (session|token|credential)/i,
  /login required/i,
];

// The network under the app. Nothing on the server is broken. Every entry must be
// anchored tightly enough that an APPLICATION failure can never match: "load failed"
// carries a \b so "upload failed"/"download failed" (real data-loss call sites in
// this repo) stay reportable.
const NETWORK_PATTERNS = [
  /err_network/i,
  /server unreachable/i,
  /network\s*error/i,
  /err_internet_disconnected/i,
  /err_connection/i,
  /err_name_not_resolved/i,
  /\b(econnaborted|econnreset|etimedout|enotfound|epipe|ehostunreach)\b/i,
  /timeout of \d+/i,
  /timeout exceeded/i,
  /request aborted/i,
  /\bload failed\b/i,
  /socket hang up/i,
];

// The browser's own fetch-failure messages, matched per ARGUMENT and anchored: the
// app has a dozen call sites whose label merely starts with "Failed to fetch …",
// and those are real failures. Only the bare browser TypeError text is network noise.
const BROWSER_NETWORK_MESSAGES = [
  /^(typeerror:?\s*)?failed to fetch\.?$/i, // Chrome
  /^(typeerror:?\s*)?load failed\.?$/i, // Safari
];

// Kept deliberately short: each entry is a failure nobody will ever look at, so each
// one needs a reason to be here.
const DROP_PATTERNS = [
  // React Flow and other resize-aware libs; already suppressed as a window error in
  // src/index.js, but it also arrives via console.error from some code paths.
  /resizeobserver loop/i,
];

// Axios/fetch error codes that mean "the network", not "the app".
const QUIET_CODES = ["ERR_NETWORK", "ECONNABORTED", "ECONNRESET", "ETIMEDOUT", "ERR_CANCELED", "ABORT_ERR"];

const QUIET_STATUSES = [401, 403];

// One shared list so isQuietError can never drift from the filter's own text rules.
const QUIET_TEXT_PATTERNS = [...AUTH_PATTERNS, ...NETWORK_PATTERNS];

// api.js appends the raw response body to err.message, so the classified text is
// partly server-controlled and unbounded. Cap it: every pattern above matches within
// the first few hundred chars of the api.js message format.
const CLASSIFY_TEXT_MAX = 4096;

// err.cause is walked at most this deep. api.js puts the axios failure exactly one
// level down; the cap exists so a cause cycle (a.cause=b, b.cause=a) can't blow the
// stack.
const MAX_CAUSE_DEPTH = 4;

const matchesAny = (text, patterns) => patterns.some((p) => p.test(text));

// Reads the diagnostic surface of a thrown value without stringifying the whole thing
// (an axios error carries the request/response bodies — PHI — in `config.data` /
// `response.data`, which is exactly what must not be copied around).
//
// Returns QUIET, REPORT (definitive — the server answered and its status decides;
// pattern words inside a 5xx response body must never downgrade it), or null
// (no structural signal; the caller may fall back to text matching).
const errorVerdict = (err, depth = 0) => {
  if (!err || typeof err !== "object" || depth > MAX_CAUSE_DEPTH) return null;
  // api.js's interceptor tags this on the 403 "No challenge" response; it is the one
  // fully reliable signal, since the reason only appears in the response body.
  if (err.sessionExpired === true) return QUIET;
  if (err.name === "CanceledError") return QUIET;
  // "AbortError" also names IndexedDB transaction aborts — dropped clinical audio,
  // which useIndexDb surfaces on purpose — so only the request-abort flavour is quiet.
  if (err.name === "AbortError" && !/transaction/i.test(err.message || "")) return QUIET;
  if (QUIET_CODES.includes(err.code)) return QUIET;
  const status =
    err.response && typeof err.response.status === "number"
      ? err.response.status
      : typeof err.status === "number"
        ? err.status
        : null;
  if (status !== null) return QUIET_STATUSES.includes(status) ? QUIET : REPORT;
  // The interceptor rethrows a new Error with the axios error as `cause`, so the code
  // and status live one level down on every API failure in this app.
  if (err.cause && err.cause !== err) return errorVerdict(err.cause, depth + 1);
  return null;
};

const textOf = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value.message === "string") return value.message;
  return "";
};

// Classifies one console.error(...) argument list. Inspects the ERROR OBJECTS as well
// as the strings, so a generic call site like
//   console.error("Error syncing web recording", e)
// is classified correctly without the call site having to know anything.
export const classifyConsoleArgs = (args) => {
  const list = Array.isArray(args) ? args : [];
  const verdicts = list.map((arg) => errorVerdict(arg));
  // A definitive server-response verdict outranks anything the text could say.
  if (verdicts.includes(REPORT)) return REPORT;
  if (verdicts.includes(QUIET)) return QUIET;
  const text = list.map(textOf).filter(Boolean).join(" ").slice(0, CLASSIFY_TEXT_MAX);
  if (text === "") return REPORT;
  if (matchesAny(text, DROP_PATTERNS)) return DROP;
  if (list.some((arg) => matchesAny(textOf(arg).trim(), BROWSER_NETWORK_MESSAGES))) return QUIET;
  if (matchesAny(text, QUIET_TEXT_PATTERNS)) return QUIET;
  return REPORT;
};

// Flag-aware check for a call site that wants to branch itself (e.g. to decide whether
// to show a toast). Same rules as the filter.
export const isQuietError = (err) => {
  const verdict = errorVerdict(err);
  if (verdict !== null) return verdict === QUIET;
  const text = textOf(err).slice(0, CLASSIFY_TEXT_MAX);
  return matchesAny(text.trim(), BROWSER_NETWORK_MESSAGES) || matchesAny(text, QUIET_TEXT_PATTERNS);
};

const BREADCRUMB_MESSAGE_MAX = 300;

// api.js embeds the request URL (query string included) and the raw response body in
// err.message. scrubSentryBreadcrumb only scrubs fetch/xhr/http categories — not this
// module's "expected-failure" — so the scrubbing has to happen here, before the text
// ever reaches a breadcrumb.
const redactErrorText = (text) =>
  String(text)
    .split(/(?:\\|\n)\s*(?:Request|Response) body:/i)[0]
    .replace(/\?\S+/g, "?[redacted]");

const looksLikeError = (value) =>
  value instanceof Error ||
  (value && typeof value === "object" && (typeof value.message === "string" || value.code || value.response || value.config));

const breadcrumbFor = (args) => {
  const list = Array.isArray(args) ? args : [];
  const labels = list.filter((arg) => typeof arg === "string");
  // Prefer the argument that carries the failure — a leading context object must not
  // shadow the actual error.
  const source = list.find(looksLikeError) || list.find((arg) => arg && typeof arg === "object");
  let data;
  if (source) {
    data = sanitizeErrorForLog(source);
    // api.js rethrows with the axios failure on `cause`; lift the structured fields
    // so the breadcrumb keeps status/method/path for triage.
    if (source.cause && typeof source.cause === "object" && source.cause !== source) {
      const causeData = sanitizeErrorForLog(source.cause);
      for (const key of ["code", "method", "url", "status", "statusText"]) {
        if (data[key] === undefined && causeData[key] !== undefined) data[key] = causeData[key];
      }
    }
    if (data.message) data.message = redactErrorText(data.message).slice(0, BREADCRUMB_MESSAGE_MAX);
  }
  if (labels.length === 0 && data && data.message) labels.push(data.message);
  return {
    category: "expected-failure",
    level: "info",
    message: redactErrorText(labels.join(" ")).slice(0, BREADCRUMB_MESSAGE_MAX) || "expected client failure",
    data,
  };
};

// Wraps console.error. Install AFTER Sentry.init so this sits outside Sentry's own
// console wrapper — a QUIET verdict must stop before it becomes an event.
//
// deps are injectable for tests only; nothing in the app passes them.
export const installClientErrorFilter = (deps = {}) => {
  const target = deps.console || (typeof console !== "undefined" ? console : null);
  if (!target || typeof target.error !== "function") return () => {};
  // Idempotence lives on the wrapper itself (not module state, which desyncs when
  // something else replaces console.error): a second install can never double-wrap.
  if (target.error.__clientErrorFilter) return () => {};

  const sentry = deps.sentry || Sentry;
  const original = target.error.bind(target);
  // Deliberately a no-op fallback: falling back to `original` would turn a QUIET
  // verdict back into the Sentry event (and native forward) it exists to prevent.
  const originalWarn = typeof target.warn === "function" ? target.warn.bind(target) : () => {};
  // Inside the native shell the console is the transport to support's inbox; in a
  // browser it is just the devtools console, so a quiet failure can still be printed.
  // Evaluated per call: some shell versions inject the bridge after page start.
  const isNativeShell = () =>
    typeof deps.isNativeShell === "boolean"
      ? deps.isNativeShell
      : typeof window !== "undefined" && !!(window.ReactNativeWebView && window.ReactNativeWebView.postMessage);

  const filtered = (...args) => {
    let verdict = REPORT;
    try {
      verdict = classifyConsoleArgs(args);
    } catch (e) {
      // Never let the filter be the reason an error goes unreported.
      verdict = REPORT;
    }

    if (verdict === DROP) return;

    if (verdict === QUIET) {
      try {
        sentry.addBreadcrumb(breadcrumbFor(args));
      } catch (e) {
        // A telemetry failure must not escalate into the thing it was reporting.
      }
      if (!isNativeShell()) {
        try {
          originalWarn(...args);
        } catch (e) {
          /* ignore */
        }
      }
      return;
    }

    original(...args);
  };
  filtered.__clientErrorFilter = true;

  target.error = filtered;

  // Restores the original — used by tests, and by anything that needs to prove the
  // filter is not swallowing something.
  return () => {
    if (target.error === filtered) target.error = original;
  };
};
